import { useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, X, ExternalLink } from "lucide-react";
import { PhonePeBadge } from "./payments/RailSelector";

const FREE_CATS = new Set(["super_stockist", "custodian", "guarantor", "introducer"]);

/**
 * PurchaseOrderButton — the copper-gold "Generate PO" pill on order details.
 *
 * Fee & privacy rules (server-authoritative):
 *   • Level-2 partners (Custodian / Introducer / Guarantor / Super-Stockist)
 *     → FREE, one-click issue.
 *   • All other buyers → INR 99 Razorpay checkout.
 *   • Buyers never see the list of Vinci suppliers — the "Vinci Ecosystem"
 *     mode leaves the supplier blank; the admin picks it later.  Buyers can
 *     alternatively use the "Own Branded" mode and type the supplier
 *     themselves.
 */
export function PurchaseOrderButton({ api, order, user }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("vinci_ecosystem");
  const [own, setOwn] = useState({
    self_supplier_name: "", self_supplier_gstin: "", self_supplier_pan: "",
    self_supplier_address: "", self_supplier_email: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const isFree = FREE_CATS.has(user?.buyer_category || "");
  const feeLabel = isFree ? "FREE · Instant" : "INR 99 · Razorpay or PhonePe";
  const [ppBusy, setPpBusy] = useState(false);

  const payPhonePe = async () => {
    setPpBusy(true);
    try {
      const r = await api.post(`/purchase-orders/${result.id}/phonepe-link`);
      window.location.assign(r.data.pay_url);
    } catch (e) { toast.error(e?.response?.data?.detail || "PhonePe could not open — try Razorpay"); setPpBusy(false); }
  };

  const submit = async () => {
    if (mode === "self_branded" && !own.self_supplier_name.trim()) {
      toast.error("Supplier name is required for a self-branded PO");
      return;
    }
    setBusy(true);
    try {
      const body = { mode, ...(mode === "self_branded" ? own : {}) };
      const r = await api.post(`/orders/${order.id}/purchase-order`, body);
      setResult(r.data);
      if (r.data.status === "issued") toast.success(`PO ${r.data.po_number} issued and emailed`);
      else if (r.data.status === "awaiting_supplier") toast.success("Request filed with the Vinci Trade Desk — you'll receive the PO once the supplier is assigned.");
      else if (r.data.status === "awaiting_payment" && r.data.payment_link_url) toast.success("Complete the ₹99 payment to receive your PO");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create PO"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid={`generate-po-btn-${order.id}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#B87333] to-[#D8B23E] text-black px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.16em] hover:opacity-95"
      >
        <FileText size={12}/> Generate PO
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center px-4" onClick={() => !busy && setOpen(false)} data-testid="po-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0a0a0a] border border-[#D8B23E]/40 rounded-sm p-6 md:p-8 relative">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-[#8A8A82] hover:text-[#F5F5F0]"><X size={18}/></button>
            <div className="mb-4">
              <div className="font-grotesk text-[10px] tracking-[0.28em] text-[#D8B23E] uppercase">Purchase Order · Order #{order.id.slice(0,8).toUpperCase()}</div>
              <div className="font-serif text-2xl mt-1">Generate Purchase Order</div>
              <div className="font-body text-[11px] text-[#8A8A82] mt-1">{feeLabel}</div>
            </div>

            {!result && (
              <>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button data-testid="po-mode-ecosystem" onClick={() => setMode("vinci_ecosystem")}
                          className={`p-3 rounded-sm border text-left ${mode === "vinci_ecosystem" ? "border-[#D8B23E] bg-[#D8B23E]/10" : "border-white/15 hover:border-[#B87333]"}`}>
                    <div className="font-body text-sm">Vinci Ecosystem</div>
                    <div className="font-body text-[10px] text-[#8A8A82] mt-1">The Vinci Trade Desk selects the supplier for you.</div>
                  </button>
                  <button data-testid="po-mode-self" onClick={() => setMode("self_branded")}
                          className={`p-3 rounded-sm border text-left ${mode === "self_branded" ? "border-[#D8B23E] bg-[#D8B23E]/10" : "border-white/15 hover:border-[#B87333]"}`}>
                    <div className="font-body text-sm">Own Branded</div>
                    <div className="font-body text-[10px] text-[#8A8A82] mt-1">Enter your supplier's details yourself.</div>
                  </button>
                </div>
                {mode === "self_branded" && (
                  <div className="space-y-2 mb-4">
                    {["self_supplier_name","self_supplier_gstin","self_supplier_pan","self_supplier_address","self_supplier_email"].map((k) => (
                      <input key={k} data-testid={`po-${k}`}
                             value={own[k]} onChange={(e) => setOwn({...own, [k]: e.target.value})}
                             placeholder={k.replace("self_supplier_","").replace("_"," ")}
                             className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 font-body text-sm outline-none focus:border-[#D8B23E]"/>
                    ))}
                  </div>
                )}
                <button
                  data-testid="po-submit"
                  disabled={busy}
                  onClick={submit}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-gradient-to-r from-[#B87333] to-[#D8B23E] text-black px-5 py-3 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-40"
                >
                  {busy ? <Loader2 size={13} className="animate-spin"/> : <FileText size={13}/>} {isFree ? "Issue PO (Free)" : "Continue · ₹99"}
                </button>
              </>
            )}
            {result && (
              <div className="mt-2" data-testid="po-result">
                {result.status === "issued" && (
                  <div className="text-[#2FB999] font-body">
                    PO <span className="font-mono">{result.po_number}</span> issued and emailed. Refresh the order to see it in history.
                  </div>
                )}
                {result.status === "awaiting_supplier" && (
                  <div className="text-[#D8B23E]">Request filed. The Vinci Trade Desk will assign a supplier and dispatch the PO to you.</div>
                )}
                {result.status === "awaiting_payment" && (
                  <div className="space-y-2" data-testid="po-pay-link">
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">Pay ₹{result.fee_inr || 99} to receive your PO</div>
                    <div className="flex flex-wrap gap-2">
                      {result.payment_link_url && (
                        <a data-testid="po-pay-razorpay" href={result.payment_link_url} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-2 rounded-sm bg-[#D8B23E] text-black px-5 py-3 font-grotesk text-[11px] uppercase tracking-[0.28em]">
                          Pay via Razorpay <ExternalLink size={13}/>
                        </a>
                      )}
                      <button type="button" data-testid="po-pay-phonepe" disabled={ppBusy} onClick={payPhonePe}
                              className="inline-flex items-center gap-2 rounded-sm bg-[#5F259F] text-white px-5 py-3 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-50">
                        {ppBusy ? <Loader2 size={13} className="animate-spin" /> : <PhonePeBadge size={13} />} Pay via PhonePe
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
